import {SegmentedControl} from '@primer/react'
import {use} from 'react'
import {CommentsFilter} from './CommentsFilter'
import {CommentsContext, ViewMode} from './CommentsContext'
import type {AuthorFilterItem} from './utils/get-author-filters'
import type {CommentsFilterState} from './use-comments-filter-state'
import {isFeatureEnabled} from '@github-ui/feature-flags'

interface CommentsViewControlsProps {
  /** All authors in the current view mode. */
  authorList: AuthorFilterItem[]
  /** Current filter state, for the current view mode. */
  filterState: CommentsFilterState
  setFilterState: (state: CommentsFilterState) => void
}

function CommentsViewSwitch() {
  const {viewMode, setViewMode} = use(CommentsContext)

  return isFeatureEnabled('prs_docked_comments_replies') ? (
    <SegmentedControl
      aria-label="Comments view mode"
      onChange={(value: number) => {
        const nextMode = [ViewMode.IssueComments, ViewMode.ThreadPreviews, ViewMode.Mine][value]
        if (nextMode) setViewMode(nextMode)
      }}
    >
      <SegmentedControl.Button selected={viewMode === ViewMode.IssueComments}>All</SegmentedControl.Button>
      <SegmentedControl.Button selected={viewMode === ViewMode.ThreadPreviews}>Unresolved</SegmentedControl.Button>
      <SegmentedControl.Button selected={viewMode === ViewMode.Mine}>Mine</SegmentedControl.Button>
    </SegmentedControl>
  ) : (
    <SegmentedControl
      aria-label="Comments view mode"
      onChange={(value: number) => {
        const nextViewMode = value === 0 ? ViewMode.ThreadPreviews : ViewMode.IssueComments
        setViewMode(nextViewMode)
      }}
    >
      <SegmentedControl.Button selected={viewMode === ViewMode.ThreadPreviews}>Review</SegmentedControl.Button>
      <SegmentedControl.Button selected={viewMode === ViewMode.IssueComments}>Conversation</SegmentedControl.Button>
    </SegmentedControl>
  )
}

/**
 * Renders view controls (filter, sort, etc) for comments.
 */
export function CommentsViewControls({authorList, filterState, setFilterState}: CommentsViewControlsProps) {
  const {viewMode} = use(CommentsContext)

  return (
    <div className="d-flex flex-row flex-items-center gap-2 py-2">
      <CommentsViewSwitch />
      <CommentsFilter
        authorList={authorList}
        className="width-full"
        filterState={filterState}
        onFilterStateChange={setFilterState}
        hideReviewThreadFilters={
          // When the flag is disabled, review thread filters are only rendered in ThreadPreviews mode, so filtering
          // only works in that mode. With it enabled, we show threads in all modes, so filtering works everywhere.
          isFeatureEnabled('prs_docked_comments_replies') ? false : viewMode !== ViewMode.ThreadPreviews
        }
      />
    </div>
  )
}
